import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { USAGE_ACTION } from '../../constants/sample.constants';
import BaseDateTimeField from './BaseDateTimeField';

export type UsageRecordEditorRowData = {
  clientKey: string;
  usageDate: string;
  action: string;
  purpose?: string;
  quantity: number;
  remainingQuantity: number;
  recordedBy?: string;
};

type UsageRecordEditorRowProps = {
  record: UsageRecordEditorRowData;
  index: number;
  sampleCreatedDate: string;
  language: string;
  onFieldChange: (index: number, field: string, value: unknown) => void;
  onRequestDelete: (index: number) => void;
};

/** サンプル編集モーダル内の使用履歴1行 */
export default function UsageRecordEditorRow({
  record,
  index,
  sampleCreatedDate,
  language,
  onFieldChange,
  onRequestDelete,
}: UsageRecordEditorRowProps) {
  const { t } = useTranslation();
  const isCreated = record.action === USAGE_ACTION.CREATED;
  const n = index + 1;

  return (
    <tr>
      <td>
        <BaseDateTimeField
          id={`usage-record-${index}-date`}
          name={`usageRecords[${index}].usageDate`}
          label={`${t('sample.usage_date')} ${n}`}
          hideLabel
          size="sm"
          value={record.usageDate}
          min={isCreated ? undefined : sampleCreatedDate || undefined}
          onChange={(v) => onFieldChange(index, 'usageDate', v)}
          lang={language}
        />
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`usage-record-${index}-action`}>
          {t('sample.action')} {n}
        </label>
        <Form.Select
          id={`usage-record-${index}-action`}
          name={`usageRecords[${index}].action`}
          size="sm"
          value={record.action}
          onChange={(e) => onFieldChange(index, 'action', e.target.value)}
          autoComplete="off"
          aria-label={`${t('sample.action')} ${n}`}
        >
          <option value={USAGE_ACTION.CREATED}>{t('sample.action_created')}</option>
          <option value={USAGE_ACTION.USE}>{t('sample.action_used')}</option>
          <option value={USAGE_ACTION.DISPOSE}>{t('sample.action_disposed')}</option>
        </Form.Select>
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`usage-record-${index}-purpose`}>
          {t('sample.detail')} {n}
        </label>
        <Form.Control
          id={`usage-record-${index}-purpose`}
          name={`usageRecords[${index}].purpose`}
          type="text"
          size="sm"
          value={record.purpose || ''}
          onChange={(e) => onFieldChange(index, 'purpose', e.target.value)}
          placeholder={t('sample.detail_placeholder')}
          autoComplete="off"
          aria-label={`${t('sample.detail')} ${n}`}
        />
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`usage-record-${index}-quantity`}>
          {t('sample.quantity_used')} {n}
        </label>
        <Form.Control
          id={`usage-record-${index}-quantity`}
          name={`usageRecords[${index}].quantity`}
          type="number"
          size="sm"
          min="0"
          value={isCreated ? '' : record.quantity}
          onChange={(e) => onFieldChange(index, 'quantity', parseInt(e.target.value, 10) || 0)}
          readOnly={isCreated}
          autoComplete="off"
          aria-label={`${t('sample.quantity_used')} ${n}`}
        />
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`usage-record-${index}-remaining`}>
          {t('sample.remaining_quantity_label')} {n}
        </label>
        <Form.Control
          id={`usage-record-${index}-remaining`}
          name={`usageRecords[${index}].remainingQuantity`}
          type="number"
          size="sm"
          min="0"
          value={record.remainingQuantity ?? ''}
          readOnly={!isCreated}
          className={isCreated ? '' : 'bg-light'}
          onChange={(e) =>
            onFieldChange(index, 'remainingQuantity', parseInt(e.target.value, 10) || 0)
          }
          autoComplete="off"
          aria-label={`${t('sample.remaining_quantity_label')} ${n}`}
        />
      </td>
      <td>
        <label className="visually-hidden" htmlFor={`usage-record-${index}-recorded-by`}>
          {t('sample.recorded_by')} {n}
        </label>
        <Form.Control
          id={`usage-record-${index}-recorded-by`}
          name={`usageRecords[${index}].recordedBy`}
          type="text"
          size="sm"
          value={record.recordedBy || ''}
          onChange={(e) => onFieldChange(index, 'recordedBy', e.target.value)}
          placeholder={t('sample.recorded_by_placeholder')}
          autoComplete="off"
          aria-label={`${t('sample.recorded_by')} ${n}`}
        />
      </td>
      <td>
        <Button
          variant="link"
          size="sm"
          type="button"
          className="text-danger p-0"
          onClick={() => onRequestDelete(index)}
          aria-label={t('common.delete')}
          title={t('sample.remove_usage_record_title', {
            n,
            defaultValue: `{{n}}行目の使用記録を削除`,
          })}
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
        </Button>
      </td>
    </tr>
  );
}
